import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { BaseEntityService } from './base-entity.service';
import { ICustomerEntityService } from '../../interfaces/entities/customer-entity-service.interface';
import { CustomerEntity } from '../../models/entities/customer';
import { ContactEntity } from '../../models/entities/contact-entity';

@Injectable({
  providedIn: 'root'
})
export class CustomerEntityService extends BaseEntityService<CustomerEntity> implements ICustomerEntityService {
  public baseUrl = environment.apiUrl;

  constructor(private readonly httpClient: HttpClient) {
    super();
  }

  protected initializeData(): void {
    // Intentionally empty: runtime source is backend API.
    this.entities = [];
  }

  createEntity(customer: CustomerEntity, createdBy: string): CustomerEntity {
    const newCustomer = new CustomerEntity();
    this.setCreateAuditProperties(newCustomer, createdBy || 'system');

    newCustomer.name = customer.name;
    newCustomer.address = customer.address;
    newCustomer.website = customer.website;
    newCustomer.gstNumber = customer.gstNumber;
    newCustomer.contactPerson = this.mapContact(customer.contactPerson);

    this.entities.push(newCustomer);
    return newCustomer;
  }

  updateEntity(id: string, updates: Partial<CustomerEntity>, updatedBy: string): CustomerEntity | null {
    const customer = this.entities.find(c => c.id === id);
    if (!customer) {
      return null;
    }

    this.setUpdateAuditProperties(customer, updatedBy || 'system');

    if (updates.name !== undefined) customer.name = updates.name;
    if (updates.address !== undefined) customer.address = updates.address;
    if (updates.website !== undefined) customer.website = updates.website;
    if (updates.gstNumber !== undefined) customer.gstNumber = updates.gstNumber;
    if (updates.contactPerson !== undefined) {
      customer.contactPerson = this.mapContact(updates.contactPerson);
    }

    return customer;
  }

  getCustomerById(id: string): CustomerEntity | undefined {
    return this.getEntityById(id);
  }

  createCustomer(customer: CustomerEntity, _createdBy: string = ''): Observable<CustomerEntity> {
    const payload = this.toApiPayload(customer);
    return this.httpClient
      .post<any>(`${this.baseUrl}/v1/customers`, payload)
      .pipe(
        map(dto => this.mapCustomer(dto)),
        tap(created => this.upsertCache(created)),
      );
  }

  updateCustomer(id: string, updates: Partial<CustomerEntity>, updatedBy: string): CustomerEntity | null {
    return this.updateEntity(id, updates, updatedBy);
  }

  updateCustomerRemote(id: string, customer: CustomerEntity): Observable<CustomerEntity> {
    const payload = this.toApiPayload(customer);
    return this.httpClient
      .put<any>(`${this.baseUrl}/v1/customers/${encodeURIComponent(id)}`, payload)
      .pipe(
        map(dto => this.mapCustomer(dto)),
        tap(updated => this.upsertCache(updated)),
      );
  }

  deleteCustomer(id: string, deletedBy: string): boolean {
    return this.deleteEntity(id, deletedBy);
  }

  getCustomerByIdRemote(id: string): Observable<CustomerEntity> {
    return this.httpClient
      .get<any>(`${this.baseUrl}/v1/customers/${encodeURIComponent(id)}`)
      .pipe(
        map(dto => this.mapCustomer(dto)),
        tap(customer => this.upsertCache(customer)),
      );
  }

  archiveCustomer(id: string): Observable<void> {
    return this.httpClient.patch<void>(
      `${this.baseUrl}/v1/customers/${encodeURIComponent(id)}/archive`,
      null,
    );
  }

  async getAllCustomers(): Promise<CustomerEntity[]> {
    const result = await lastValueFrom(this.httpClient.get<any[]>(`${this.baseUrl}/v1/customers`));
    const customers = (result ?? []).map(dto => this.mapCustomer(dto));
    this.entities = customers;
    return customers;
  }

  private upsertCache(customer: CustomerEntity): void {
    const index = this.entities.findIndex(c => c.id === customer.id);
    if (index === -1) {
      this.entities.push(customer);
      return;
    }

    this.entities[index] = customer;
  }

  private mapCustomer(dto: any): CustomerEntity {
    const customer = new CustomerEntity();

    customer.id = String(dto?.id ?? dto?._id ?? '');
    customer.name = String(dto?.name ?? dto?.Name ?? '');
    customer.address = String(dto?.address ?? dto?.Address ?? '');
    customer.website = String(dto?.webSite ?? dto?.website ?? dto?.WebSite ?? '');
    customer.gstNumber = String(dto?.gstNumber ?? dto?.GstNumber ?? '');
    customer.isActive = !(dto?.isArchived ?? dto?.IsArchived ?? false);

    const createdRaw = dto?.createdDate ?? dto?.CreatedDate ?? dto?.createdOn ?? dto?.CreatedOn;
    const updatedRaw = dto?.updateDate ?? dto?.UpdateDate ?? dto?.updatedOn ?? dto?.UpdatedOn;

    customer.createdOn = createdRaw ? new Date(createdRaw) : customer.createdOn;
    customer.updatedOn = updatedRaw ? new Date(updatedRaw) : customer.updatedOn;

    customer.contactPerson = this.mapContact(dto?.contactPerson ?? dto?.ContactPerson);

    return customer;
  }

  private mapContact(source: any): ContactEntity {
    const contact = new ContactEntity();
    if (!source) {
      return contact;
    }

    contact.id = String(source.id ?? source.Id ?? '');
    contact.name = String(source.name ?? source.Name ?? '');
    contact.email = String(source.email ?? source.Email ?? '');
    contact.mobile = String(source.mobile ?? source.phone ?? source.Mobile ?? source.Phone ?? '');

    return contact;
  }

  private toApiPayload(customer: CustomerEntity): any {
    return {
      id: customer.id,
      name: customer.name?.trim(),
      address: customer.address?.trim() ?? '',
      webSite: customer.website?.trim() ?? '',
      gstNumber: customer.gstNumber?.trim() ?? '',
      contactPerson: {
        name: customer.contactPerson?.name?.trim() ?? '',
        email: customer.contactPerson?.email?.trim() ?? '',
        mobile: customer.contactPerson?.mobile?.trim() ?? '',
      }
    };
  }
}
